import React, { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  Button,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import BarcodeScanner from "@/components/thirdParty/react-barcode-reader";
import DashPage from "../dash/DashPage";
import { SessionContext } from "@/utility/useSession";
import { useTranslation } from "react-i18next";
import NotFoundPage from "../error/NotFoundPage";
import { IDriverMember } from "./components/card/card";
import TimelineModal from "./components/timelineModal";
import { useGetShiftData } from "./hooks/useGetShiftData";
import { useGetShiftTasks } from "@/hooks/useGetShiftTasks";
import { useAddShiftEvent } from "@/hooks/useAddShiftEvent";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { ShiftCategory, TaskCategory } from "@/logic/api";
import AccessDeniedMessage from "@/components/errors/AccessDeniedMessage";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";
import { hasKeyMasterPermissions } from "@/utility/utils";
import { SnackAlert } from "@/components/notifications/snack/SnackAlert";
import { useDriverScanner } from "./hooks/useDriverScanner";
import DriverScannerModal from "./components/driverScannerModal";
import FilterBar from "./components/FilterBar";
import SummaryStats from "./components/SummaryStats";
import DriverGrid from "./components/DriverGrid";
import CurrentTimeDisplay from "./components/CurrentTimeDisplay";
import { useDriverFilters } from "./hooks/useDriverFilters";
import { useDriverSummary } from "./hooks/useDriverSummary";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

const DriverShiftTrackerContent = () => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isCarParkSpecificReleased = useReleaseManagement(ReleaseFlag.releaseDriverAvailabilityCarSpecific);

  const carParkUuid = session.current?.site?.home?.uuid;

  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<IDriverMember | null>(null);
  const [shiftNotification, setShiftNotification] = useState<string>("");
  const [pendingDriverUuids, setPendingDriverUuids] = useState<Set<string>>(new Set());
  const hasAnimatedRef = useRef(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const previousCarParkRef = useRef<string | undefined>(carParkUuid);

  const canEditDriverStatus = isRbacReleased
    ? hasPermission("shiftTracking.shift.update")
    : hasKeyMasterPermissions(session);

  const {
    data: shiftData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetShiftData();
  const { data } = useGetShiftTasks();
  const addShiftEvent = useAddShiftEvent();

  const tasks = data ?? [];
  // we only need driving and nonDriving tasks for the UI
  const filteredTasks = tasks.filter(
    task => task.category === TaskCategory.Driving || task.category === TaskCategory.NonDriving
  );

  const driverList = useMemo(() => {
    if (!shiftData?.data) return [];

    return shiftData.data.map(shift => {
      const assignmentValue = shift.currentAssignment?.eventAction || "";
      const validAssignment = tasks.some((task) => task.key === assignmentValue)
        ? assignmentValue
        : (tasks[0]?.key || "");

      return {
        uuid: shift.user.uuid || "",
        name: `${shift.user.firstName || ""} ${shift.user.lastName || ""}`.trim() || "Unknown",
        assignment: validAssignment,
        status: (!shift.shiftUuid || !shift.period.startDate)
          ? "off-shift" as const
          : (isCarParkSpecificReleased && shift.carPark?.uuid != null && shift.carPark.uuid !== carParkUuid)
            ? "off-shift" as const
            : (shift.lastBreak.startDate && !shift.lastBreak.endDate)
              ? "on-break" as const
              : "on-shift" as const,
        statusStartTime: shift.period.startDate?.toString(),
        breakStartTime: shift.lastBreak.startDate?.toString(),
        shiftCarParkName: (isCarParkSpecificReleased && shift.shiftUuid && shift.period.startDate
          && shift.carPark?.uuid != null && shift.carPark.uuid !== carParkUuid)
          ? (shift.carPark.name ?? undefined)
          : undefined,
      };
    });
  }, [shiftData, tasks, carParkUuid, isCarParkSpecificReleased]);

  // TODO: DD - fix animation issue
  useEffect(() => {
    if (!hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (isCarParkSpecificReleased && previousCarParkRef.current && carParkUuid
      && previousCarParkRef.current !== carParkUuid) {
      const carParkName = session.current?.site?.home?.name ?? "";
      setShiftNotification(
        t("driverShiftTracker.notifications.carParkChanged", { name: carParkName })
      );
    }
    previousCarParkRef.current = carParkUuid;
  }, [carParkUuid]);

  const {
    filter,
    setFilter,
    roleFilter,
    setRoleFilter,
    searchQuery,
    setSearchQuery,
    filteredDrivers,
  } = useDriverFilters(driverList);

  const summary = useDriverSummary(driverList, tasks);

  const handleStatusChange = async (uuid: string, newStatus: "on-shift" | "on-break" | "off-shift") => {
    const now = new Date().toISOString();
    const currentDriver = driverList.find((driver) => driver.uuid === uuid);

    if (!carParkUuid) {
      setShiftNotification(t("driverShiftTracker.notifications.carParkUnavailable"));
      return;
    }

    if (currentDriver?.status === newStatus) {
      throw new Error(t("driverShiftTracker.notifications.alreadyInStatus", { status: newStatus }));
    }

    setPendingDriverUuids((prev) => new Set(prev).add(uuid));
    try {
      // Starting a shift: off-shift -> on-shift
      if (newStatus === "on-shift" && currentDriver?.status === "off-shift") {
        await addShiftEvent.mutateAsync({
          userUuid: uuid,
          createdByUuid: session?.current?.site?.user?.uuid ?? "",
          category: ShiftCategory.Shift,
          action: TaskCategory.On,
          occurredAt: now,
        });
        setShiftNotification(
          t("driverShiftTracker.notifications.shiftStarted", { name: currentDriver?.name ?? "" })
        );
      }
      // Ending a shift: on-shift or on-break -> off-shift
      else if (newStatus === "off-shift" && (currentDriver?.status === "on-shift" || currentDriver?.status === "on-break")) {
        await addShiftEvent.mutateAsync({
          userUuid: uuid,
          createdByUuid: session?.current?.site?.user?.uuid ?? "",
          category: ShiftCategory.Shift,
          action: TaskCategory.Off,
          occurredAt: now,
        });
        setShiftNotification(
          t("driverShiftTracker.notifications.shiftEnded", { name: currentDriver?.name ?? "" })
        );
      }
      // Break transitions: on-shift -> on-break or on-break -> on-shift
      else if (newStatus === "on-break" || (currentDriver?.status === "on-break" && newStatus === "on-shift")) {
        const shiftUuid = shiftData?.data.find(s => s.user.uuid === uuid)?.shiftUuid;

        if (!shiftUuid) {
          setShiftNotification(t("driverShiftTracker.notifications.shiftInfoUnavailable"));
          setPendingDriverUuids((prev) => {
            const next = new Set(prev);
            next.delete(uuid);
            return next;
          });
          return;
        }

        await addShiftEvent.mutateAsync({
          userUuid: uuid,
          shiftUuid,
          createdByUuid: session?.current?.site?.user?.uuid ?? "",
          occurredAt: now,
          category: ShiftCategory.Break,
          action: newStatus === "on-break" ? TaskCategory.On : TaskCategory.Off,
        });

        if (newStatus === "on-break") {
          setShiftNotification(
            t("driverShiftTracker.notifications.breakStarted", { name: currentDriver?.name ?? "" })
          );
        } else {
          setShiftNotification(
            t("driverShiftTracker.notifications.breakEnded", { name: currentDriver?.name ?? "" })
          );
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : String(error);

      setShiftNotification(
        t("driverShiftTracker.notifications.updateFailed", { message: errorMessage })
      );
    } finally {
      setPendingDriverUuids((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  const handleTaskChange = async (uuid: string, newTask: string, providedShiftUuid?: string) => {
    const shiftUuid = providedShiftUuid ?? shiftData?.data.find((shift) => shift.user.uuid === uuid)?.shiftUuid;

    if (!shiftUuid) {
      setShiftNotification(t("driverShiftTracker.notifications.shiftInfoUnavailable"));
      return;
    }

    setPendingDriverUuids((prev) => new Set(prev).add(uuid));
    try {

      await addShiftEvent.mutateAsync({
        userUuid: uuid,
        shiftUuid,
        category: ShiftCategory.Assignment,
        action: newTask,
        createdByUuid: session?.current?.site?.user?.uuid ?? "",
        occurredAt: new Date().toISOString(),
      });

      setShiftNotification(t("driverShiftTracker.notifications.taskUpdatedSuccessfully"));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setShiftNotification(
        t("driverShiftTracker.notifications.taskUpdateFailed", { message: errorMessage })
      );
    } finally {
      setPendingDriverUuids((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  const handleViewTimeline = (uuid: string) => {
    const driver = driverList.find((driver) => driver.uuid === uuid);
    if (driver) {
      setSelectedDriver(driver);
      setTimelineModalOpen(true);
    }
  };

  const handleCloseTimeline = () => {
    setTimelineModalOpen(false);
    setSelectedDriver(null);
  };

  const scanner = useDriverScanner({
    onStatusChange: handleStatusChange,
    onTaskChange: handleTaskChange,
    tasks,
  });

  useKeyboardShortcuts({
    "1": () => setFilter("on-shift"),
    "2": () => setFilter("on-break"),
    "3": () => setFilter("all"),
    "/": () => searchInputRef.current?.focus(),
  });

  return (
    <Box
      data-testid="driver-shift-tracker-page"
      sx={{
        maxWidth: { xs: "100%", xl: 1600 },
        margin: "0 auto",
        padding: 3,
        paddingX: { xs: 3, xl: 6 },
      }}
    >
      <Box sx={{ textAlign: "center", paddingY: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          {t("driverShiftTracker.title")}
        </Typography>
        <CurrentTimeDisplay />
      </Box>

      {isError && (
        <Alert
          severity="error"
          sx={{ marginBottom: 3 }}
          data-testid="driver-list-error-alert"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()} data-testid="driver-list-error-retry-button">
              {t("general.retry")}
            </Button>
          }
        >
          {error?.message ?? t("driverShiftTracker.error.failedToLoadDriverData")}
        </Alert>
      )}

      <SummaryStats summary={summary} isLoading={isLoading} />

      <FilterBar
        filter={filter}
        onFilterChange={setFilter}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        tasks={filteredTasks}
        searchInputRef={searchInputRef}
        summary={summary}
      />

      <DriverGrid
        drivers={filteredDrivers}
        isLoading={isLoading}
        onStatusChange={handleStatusChange}
        onTaskChange={handleTaskChange}
        onViewTimeline={handleViewTimeline}
        canEdit={canEditDriverStatus}
        tasks={filteredTasks}
        pendingDriverUuids={pendingDriverUuids}
        hasAnimated={hasAnimatedRef.current}
      />

      <BarcodeScanner
        onScan={scanner.handleScan}
        onError={() => { }}
        minLength={6}
      />

      <DriverScannerModal
        state={scanner.state}
        timeoutSeconds={scanner.timeoutSeconds}
        onClose={scanner.handleCloseModal}
        onReset={scanner.resetScanner}
        onScan={scanner.handleScan}
      />

      <TimelineModal
        open={timelineModalOpen}
        onClose={handleCloseTimeline}
        driver={selectedDriver}
      />

      {shiftNotification && (
        <SnackAlert
          id="shift-notification"
          message={shiftNotification}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          onClose={() => setShiftNotification("")}
        />
      )}
    </Box>
  );
};

const DriverShiftTrackerPage = () => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const isDriverShiftTrackerReleased = useReleaseManagement(ReleaseFlag.releaseDriverShiftTracker);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);

  if (session.current.site == null) {
    return null;
  }

  if (!session.current.site.preferences.driver_shift_tracker_enabled) {
    return <NotFoundPage title={t("notFoundPage.notAvailable")} message={t("notFoundPage.featureNotEnabled")} />;
  }

  const hasAccess = isDriverShiftTrackerReleased && (
    isRbacReleased
      ? hasPermission("shiftTracking.shift.view")
      : hasKeyMasterPermissions(session)
  );

  if (!hasAccess) {
    return (
      <DashPage>
        <AccessDeniedMessage />
      </DashPage>
    );
  }

  return (
    <DashPage>
      <DriverShiftTrackerContent />
    </DashPage>
  );
};

export default DriverShiftTrackerPage;
